import { Injectable, Injector, inject } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { API_BASE } from '../../../constants/general.constants';
import { AppStateService } from '../../app-state/app-state.service';
import type { StepFileStore } from '../../step-files/step-file-store';

/**
 * WasmModuleService — loads and executes jco-transpiled WASM modules
 * in the browser using the instantiation API.
 *
 * Host imports connect modules to Enlighten's data layer:
 * - host-files: per-module in-memory file store (browser has no direct FS)
 * - host-variables: the current step's `data`, JSON-encoded across the boundary
 *   (the same semantics as enoch, which backs it with globalThis.data)
 * - host-arrow: per-module in-memory Arrow IPC store
 * - host-log: configurable log callback (defaults to console)
 * - host-api: synchronous service calls and step queries via sync XHR, against
 *   the current project
 *
 * The Rhai step interpreter is loaded into a registry of its own, apart from
 * project modules, with its own host-files and host-modules (EHQ-399).
 *
 * See: ehq-wilkins/dev-docs/guides/global-service-protocol.md
 */

/** Manifest returned by a module's getManifest() */
export interface ModuleManifest {
  name: string;
  version: string;
  description?: string;
  functions: ModuleFunction[];
}

export interface ModuleFunction {
  name: string;
  description?: string;
  inputs: { name: string; type: string; required?: boolean; default?: unknown; description?: string }[];
  outputs: { name: string; type: string; description?: string }[];
}

/** A loaded WASM module instance */
export interface LoadedModule {
  manifest: ModuleManifest;
  getManifest: () => string;
  execute: (functionName: string, argsJson: string) => string;
  /** In-memory Arrow IPC table store (host-arrow) */
  tables: Map<string, Uint8Array>;
  /** In-memory file store (host-files) */
  files: Map<string, Uint8Array>;
}

/** A jco-transpiled component in instantiation mode. */
type JcoModule = {
  instantiate: (getCoreModule: CoreModuleLoader, imports: unknown) => Promise<any>;
};

/** Fetches and compiles one of a component's core .wasm files. */
type CoreModuleLoader = (path: string) => Promise<WebAssembly.Module>;

/** Log callback type for host-log */
export type WasmLogCallback = (level: string, message: string) => void;

/** How many bytes an OutputStream accepts per write; anything comfortably large. */
const OUTPUT_STREAM_CAPACITY = BigInt(1024 * 1024);

/**
 * Minimal WASI stubs for the WASM Component Model runtime.
 *
 * Stream methods follow jco's result convention: a method whose WIT returns a
 * `result` returns the Ok value itself, and jco wraps it; throwing is the Err arm.
 */
function createWasiStubs(): Record<string, unknown> {
  class Pollable {
    block() { /* intentional no-op: WASI stub */ }
    ready() { return true; }
  }
  /**
   * stdout/stderr, for whatever Rust's std writes there (a panic message, a
   * stray println). Rhai's print() wrote here through check-write, write and
   * blocking-flush until minerva 5f9136a sent it to host-log (EHQ-399); the old
   * stub had only the last two, so every print() trapped (EHQ-76). Text goes to
   * the console a line at a time. enoch drops a step's stdout too, so it never
   * reaches the step.
   *
   * jco may hand a method a bare Object.create(OutputStream.prototype), which has
   * no instance fields, so none of them can be relied on to exist.
   */
  class OutputStream {
    private readonly sink?: (line: string) => void;
    private decoder?: TextDecoder;
    private pending?: string;

    constructor(sink?: (line: string) => void) {
      this.sink = sink;
    }

    checkWrite(): bigint { return OUTPUT_STREAM_CAPACITY; }

    write(contents: Uint8Array): void {
      if (!this.sink) return;
      this.decoder ??= new TextDecoder();
      const lines = ((this.pending ?? '') + this.decoder.decode(contents, { stream: true })).split('\n');
      this.pending = lines.pop();
      lines.forEach(line => this.sink?.(line));
    }

    blockingWriteAndFlush(contents: Uint8Array): void {
      this.write(contents);
      this.flush();
    }

    /** Hands on a trailing partial line, if any. */
    flush(): void {
      if (this.pending) this.sink?.(this.pending);
      this.pending = '';
    }

    blockingFlush(): void { this.flush(); }

    subscribe(): Pollable { return new Pollable(); }
  }
  class InputStream {
    read(_len: unknown) { return new Uint8Array(0); }
    blockingRead(_len: unknown) { return new Uint8Array(0); }
    subscribe(): Pollable { return new Pollable(); }
  }
  class TerminalInput { /* intentional: WASI stub class */ }
  class TerminalOutput { /* intentional: WASI stub class */ }

  return {
    'wasi:cli/environment': { getEnvironment() { return []; }, getArguments() { return []; } },
    'wasi:cli/exit': { exit(_s: unknown) {} },
    'wasi:cli/stderr': { getStderr() { return new OutputStream(line => console.error('[WASM stderr]', line)); } },
    'wasi:cli/stdin': { getStdin() { return new InputStream(); } },
    'wasi:cli/stdout': { getStdout() { return new OutputStream(line => console.log('[WASM stdout]', line)); } },
    'wasi:cli/terminal-input': { TerminalInput },
    'wasi:cli/terminal-output': { TerminalOutput },
    'wasi:cli/terminal-stderr': { getTerminalStderr() { return undefined; } },
    'wasi:cli/terminal-stdin': { getTerminalStdin() { return undefined; } },
    'wasi:cli/terminal-stdout': { getTerminalStdout() { return undefined; } },
    'wasi:io/error': { Error: class { toDebugString() { return 'io-error'; } } },
    'wasi:io/poll': { Pollable, poll(_l: unknown) { return [0]; } },
    'wasi:io/streams': { InputStream, OutputStream },
    'wasi:random/insecure-seed': { insecureSeed() { return [BigInt(42), BigInt(0)]; } },
    // Rust's std pulls these in (EHQ-76: the Rhai step component imports them). No
    // preopens are offered, so nothing ever constructs a Descriptor; the class only
    // has to exist. ehq-minerva's tests/component/web_step_test.ts pins the full set.
    'wasi:random/random': {
      getRandomU64() { return crypto.getRandomValues(new BigUint64Array(1))[0]; },
    },
    'wasi:filesystem/preopens': { getDirectories() { return []; } },
    'wasi:filesystem/types': { Descriptor: class { /* intentional: WASI stub class */ } },
    'wasi:clocks/monotonic-clock': {
      now() { return BigInt(0); },
      resolution() { return BigInt(1000000); },
      subscribeDuration(_d: unknown) { return new Pollable(); },
      subscribeInstant(_i: unknown) { return new Pollable(); },
    },
    'wasi:clocks/wall-clock': {
      now() { return { seconds: BigInt(Math.floor(Date.now() / 1000)), nanoseconds: 0 }; },
      resolution() { return { seconds: BigInt(1), nanoseconds: 0 }; },
    },
  };
}

/**
 * Decode a host-variables value. The WIT carries JSON; a module that writes a
 * non-JSON string still gets it stored, as a string, rather than an error.
 */
function decodeVariable(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

/** One synchronous POST's outcome; the browser twin of enoch's HostApiResult. */
export interface HostPostResult {
  /** True for a 2xx answer. */
  ok: boolean;
  status?: number;
  body?: string;
  /** Set when there was no HTTP answer at all (network failure). */
  error?: string;
}

/**
 * A synchronous POST, because WIT imports must be synchronous, with the signed-in
 * user's token. OAuthService is looked up per call so the service has no hard
 * dependency on auth being configured. Never throws: a network failure comes back
 * as `error`, a non-2xx answer as `ok: false` with its status and body.
 *
 * No timeout: a window may not set one on a synchronous XHR (the browser throws
 * InvalidAccessError), so the request waits as long as the server does.
 */
function syncPost(injector: Injector): (url: string, body: string) => HostPostResult {
  return (url: string, body: string): HostPostResult => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, false); // synchronous
    xhr.setRequestHeader('Content-Type', 'application/json');
    const token = injector.get(OAuthService, null)?.getAccessToken();
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }
    try {
      xhr.send(body);
    } catch (e) {
      return { ok: false, error: e instanceof Error ? e.message : String(e) };
    }
    return { ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, body: xhr.responseText };
  };
}

/** The default host-api.call transport, over syncPost. */
function syncServiceCall(post: (url: string, body: string) => HostPostResult): (url: string, argsJson: string) => string {
  return (url: string, argsJson: string): string => {
    const result = post(url, argsJson);
    if (result.ok) {
      return result.body ?? '';
    }
    throw new Error(`Service call failed: ${result.error ?? `${result.status} ${result.body}`}`);
  };
}

/**
 * Pull the human-readable message out of an ehq-server error body ({ code,
 * message, details }), falling back to the raw text when it is not that shape.
 * Twin of enoch's serverErrorMessage (wasm-host-imports.ts).
 */
export function serverErrorMessage(body: string | undefined): string {
  if (!body) return 'unknown error';
  try {
    const parsed = JSON.parse(body);
    const message = parsed?.message ?? parsed?.error ?? parsed?.description;
    if (typeof message === 'string' && message.length > 0) return message;
  } catch {
    // Not JSON; the text itself is the best message there is.
  }
  return body;
}

/**
 * The body of host-api.query (EHQ-358): run SQL against a project connection by
 * name through ehq-server's step-query endpoint, the same contract enoch's
 * runStepQuery uses (EHQ-285). POSTs { connection, sql, params } to
 * /project/{id}/query; the server answers { rows, rowCount } and the WIT contract
 * is the rows alone. maxRows is left to the server default, as enoch does, and a
 * result over the cap is the server's 400, never a truncation.
 *
 * Throws plain Errors labelled with the connection; the import wraps them in
 * hostError so they reach the module as the WIT Err arm.
 */
export function runStepQuery(
  post: (url: string, body: string) => HostPostResult,
  apiBase: string,
  projectId: number,
  connection: string,
  sql: string,
  paramsJson: string,
): string {
  const label = `Query on ${connection}`;
  let params: unknown;
  try {
    params = JSON.parse(paramsJson || '[]');
  } catch (e) {
    throw new Error(`${label}: params are not valid JSON: ${e}`);
  }
  if (!Array.isArray(params)) {
    throw new Error(`${label}: params must be a JSON array`);
  }

  const result = post(`${apiBase}/project/${projectId}/query`, JSON.stringify({ connection, sql, params }));
  if (!result.ok) {
    throw new Error(`${label} failed: ${result.error ?? serverErrorMessage(result.body)}`);
  }

  let reply: { rows?: unknown };
  try {
    reply = JSON.parse(result.body ?? '');
  } catch {
    throw new Error(`${label} failed: server reply is not JSON`);
  }
  if (!Array.isArray(reply?.rows)) {
    throw new Error(`${label} failed: server reply has no rows array`);
  }
  return JSON.stringify(reply.rows);
}

/**
 * An error a WASM host import can throw that jco turns into the WIT `result`'s
 * Err arm, so the module can handle it (a Rhai script can catch it). jco's
 * generated `getErrorPayload` returns a thrown value's own `payload` as the Err,
 * but rethrows a plain `Error` as a trap, which bypasses the module entirely.
 * Every import whose WIT signature returns a `result` must throw this rather
 * than a plain `Error`. Still an `Error`, so callers outside jco are unaffected.
 */
export function hostError(message: string): Error & { payload: string } {
  return Object.assign(new Error(message), { payload: message });
}

/**
 * The error for a host import the browser does not implement for the Rhai
 * interpreter, worded like EHQ-285's original host-api.query rejection so every
 * browser-only gap reads the same and names the way out.
 */
export function browserUnsupported(op: string, detail: string): string {
  return `${op} is not supported in the browser yet (${detail}); run this step headless`;
}

type HostImports = Record<string, Record<string, (...args: any[]) => unknown>>;

/**
 * The imports the Rhai interpreter (minerva-step) gets in place of, or on top of,
 * an ordinary module's (EHQ-399).
 *
 * host-files: backed by the step's StepFileStore (EHQ-519), which has enoch's
 * rules: reads see the job's files (the user's cache) over the workflow's default
 * files over the project's, writes go to the job's files, `tables/` is refused.
 * With no store (nothing is running a step), every function refuses.
 *
 * host-tables: `ehq::read_table` (table-info) reads `tables/<name>` through the
 * same store. `ehq::query_table` streams up to a gigabyte, which a synchronous
 * request on the main thread cannot sensibly do, so it stays headless-only
 * (EHQ-524).
 *
 * host-modules: the interpreter calling a project module waits on the browser
 * loading project modules at all (EHQ-515).
 *
 * Every failure is the WIT Err arm (hostError), so a script can catch it. The
 * messages are enoch's, so a step fails the same way in both runtimes.
 */
export function interpreterHostImports(getStore: () => StepFileStore | null): HostImports {
  const refuse = (op: string, detail: string): never => {
    throw hostError(browserUnsupported(op, detail));
  };
  const message = (e: unknown) => (e instanceof Error ? e.message : String(e));
  /** Run `fn` against the store, or refuse when no step is running. */
  const withStore = <T>(op: string, detail: string, failure: string, fn: (store: StepFileStore) => T): T => {
    const store = getStore();
    if (!store) return refuse(op, detail);
    try {
      return fn(store);
    } catch (e) {
      throw hostError(failure ? `${failure}: ${message(e)}` : message(e));
    }
  };
  return {
    'enlighten:module/host-files': {
      readFile: (path: string) =>
        withStore('host-files.read-file', `file '${path}'`, `Failed to read file '${path}'`,
          (s) => s.readFile(path)),
      readFileChunk: (path: string, offset: bigint, length: number) =>
        withStore('host-files.read-file-chunk', `file '${path}'`,
          `Failed to read chunk from '${path}' at offset ${offset}`,
          (s) => s.readFileChunk(path, offset, length)),
      fileSize: (path: string) =>
        withStore('host-files.file-size', `file '${path}'`, `Failed to get file size for '${path}'`,
          (s) => s.fileSize(path)),
      writeFile: (path: string, data: Uint8Array) =>
        withStore('host-files.write-file', `file '${path}'`, `Failed to write file '${path}'`,
          (s) => s.writeFile(path, data)),
      listFiles: (dir: string) =>
        withStore('host-files.list-files', `directory '${dir}'`, `Failed to list files in '${dir}'`,
          (s) => s.listFiles(dir)),
    },
    'enlighten:module/host-modules': {
      execute: (name: string, functionName: string) =>
        refuse('host-modules.execute', `module '${name}', function '${functionName}'`),
    },
    'enlighten:module/host-tables': {
      queryTable: (connection: string, _sql: string, _paramsJson: string, name: string) =>
        refuse('host-tables.query-table', `table '${name}' on '${connection}'`),
      tableInfo: (name: string) =>
        withStore('host-tables.table-info', `table '${name}'`, '',
          (s) => JSON.stringify(s.tableInfo(name))),
    },
  };
}

/**
 * True when an error out of a component call is a trap rather than the export's
 * own Err arm. jco throws the Err arm as a ComponentError carrying an own
 * `payload`; anything else (a WebAssembly.RuntimeError, or a host import that threw
 * where its WIT has no Err arm) unwound through the guest mid-call, and the
 * instance must not be used again.
 */
export function isTrap(e: unknown): boolean {
  return !(e instanceof Error && Object.prototype.hasOwnProperty.call(e, 'payload'));
}

@Injectable({
  providedIn: 'root',
})
export class WasmModuleService {
  /** Project modules: what listModules() reports and the JS/Python `modules` bindings expose. */
  private readonly modules = new Map<string, LoadedModule>();
  /**
   * Step interpreters (EHQ-399): the Rhai runtime, kept apart from project modules
   * so it never appears in listModules() or the `modules` bindings, cannot be run
   * through executeFunction(), and a project module of the same name cannot
   * replace it.
   */
  private readonly interpreters = new Map<string, LoadedModule>();
  private readonly moduleCache = new Map<string, WebAssembly.Module>();
  private readonly injector = inject(Injector);

  /**
   * The step's workflow variables, as host-variables sees them. Values are plain JS
   * values and cross the WIT boundary JSON-encoded, exactly as enoch does it over
   * globalThis.data, so `set_variable("n", "7")` stores the number 7.
   *
   * A runtime that runs a module-backed step assigns the step's data here before
   * the call and reads it back after. Only the Rhai runtime does so today; module
   * calls from JS and Python steps wait on modules loading at all (EHQ-294).
   */
  data: Record<string, unknown> = {};

  /**
   * The running step's files (EHQ-519), as the interpreter's host-files and
   * host-tables see them. A runtime sets it for the length of a step and clears
   * it after; with none, those imports refuse.
   */
  fileStore: StepFileStore | null = null;

  /** Log callback — defaults to console, can be set by step execution context */
  logCallback: WasmLogCallback = (level: string, message: string) => {
    const prefix = `[WASM ${level}]`;
    switch (level) {
      case 'error': console.error(prefix, message); break;
      case 'warn': console.warn(prefix, message); break;
      default: console.log(prefix, message);
    }
  };

  /**
   * Transport for host-api::call(). Sync XHR because WIT imports must be
   * synchronous; replaceable in tests.
   * See: ehq-wilkins/dev-docs/guides/global-service-protocol.md
   */
  serviceCallFn: ((url: string, argsJson: string) => string) | null = syncServiceCall(syncPost(this.injector));

  /**
   * Transport for host-api::query(). Sync XHR for the same reason as
   * serviceCallFn; replaceable in tests.
   */
  queryPostFn: (url: string, body: string) => HostPostResult = syncPost(this.injector);

  /**
   * The project host-api calls go to, read at call time so a project switch is
   * followed. It used to be captured once when Pyodide initialized, which went
   * stale on a switch and left host-api unconfigured until Python had loaded.
   */
  projectIdFn: () => number = () => this.injector.get(AppStateService, null)?.projectId() ?? 0;

  /**
   * Load a module from an assets path.
   * The path should be relative to the app root, e.g., 'assets/local_wasm/demo-arrow'.
   * The directory must contain module.js (instantiation mode) and module.core.wasm.
   */
  async loadModule(assetPath: string): Promise<LoadedModule> {
    const { moduleJs, getCoreModule } = await this.importAsset(assetPath);
    return this.instantiateModule(moduleJs, getCoreModule);
  }

  /**
   * Load a step interpreter from an assets path into the interpreter registry
   * (EHQ-399), with the interpreter's host imports. See interpreterHostImports().
   */
  async loadInterpreter(assetPath: string): Promise<LoadedModule> {
    const { moduleJs, getCoreModule } = await this.importAsset(assetPath);
    return this.instantiateInterpreter(moduleJs, getCoreModule);
  }

  private async importAsset(assetPath: string): Promise<{ moduleJs: JcoModule; getCoreModule: CoreModuleLoader }> {
    // Dynamic import of the jco-transpiled module (instantiation mode)
    // @ts-ignore - dynamic path intentional for runtime module loading
    const moduleJs = await import(
      /* webpackIgnore: true */
      /* @vite-ignore */
      `/${assetPath}/module.js`
    );

    // getCoreModule callback — fetches and compiles .wasm files
    const baseUrl = `/${assetPath}/`;
    const getCoreModule = async (path: string): Promise<WebAssembly.Module> => {
      const cacheKey = `${assetPath}/${path}`;
      if (this.moduleCache.has(cacheKey)) {
        return this.moduleCache.get(cacheKey)!;
      }
      const url = `${baseUrl}${path}`;
      const response = await fetch(url);
      // deepcode ignore javascript/CodeInjection: compiles a first-party .wasm asset fetched from a same-origin relative path (app bundle), not remote user input
      const module = await WebAssembly.compileStreaming(response);
      this.moduleCache.set(cacheKey, module);
      return module;
    };

    return { moduleJs, getCoreModule };
  }

  /**
   * Instantiate an already-imported jco module (instantiation mode) with the host
   * imports and register it. Split from loadModule() so a test can hand in a module
   * and core-module loader that read from disk rather than over HTTP.
   */
  async instantiateModule(moduleJs: JcoModule, getCoreModule: CoreModuleLoader): Promise<LoadedModule> {
    const loaded = await this.instantiate(moduleJs, getCoreModule, {});
    this.modules.set(loaded.manifest.name, loaded);
    console.log(`[WasmModuleService] Loaded: ${loaded.manifest.name}@${loaded.manifest.version} (${loaded.manifest.functions.length} functions)`);
    return loaded;
  }

  /**
   * instantiateModule() for a step interpreter: interpreterHostImports() replace
   * the ordinary host-files and add host-modules, and the result goes to the
   * interpreter registry, never the project-module one.
   */
  async instantiateInterpreter(moduleJs: JcoModule, getCoreModule: CoreModuleLoader): Promise<LoadedModule> {
    const loaded = await this.instantiate(moduleJs, getCoreModule, interpreterHostImports(() => this.fileStore));
    this.interpreters.set(loaded.manifest.name, loaded);
    console.log(`[WasmModuleService] Loaded interpreter: ${loaded.manifest.name}@${loaded.manifest.version}`);
    return loaded;
  }

  private async instantiate(
    moduleJs: JcoModule,
    getCoreModule: CoreModuleLoader,
    overrides: HostImports,
  ): Promise<LoadedModule> {
    // Per-module storage for host-arrow and host-files
    const tables = new Map<string, Uint8Array>();
    const files = new Map<string, Uint8Array>();

    // Read through `this` at call time: a runtime swaps `data` per step.
    const getData = () => this.data;
    const getLogCallback = () => this.logCallback;

    // Build imports object with WASI stubs and real host imports
    const imports = {
      ...createWasiStubs(),
      'enlighten:module/host-arrow': {
        readTable(name: string): Uint8Array {
          const data = tables.get(name);
          if (!data) throw hostError(`Table '${name}' not found`);
          return data;
        },
        writeTable(name: string, data: Uint8Array): void {
          tables.set(name, new Uint8Array(data));
        },
      },
      'enlighten:module/host-files': {
        readFile(path: string): Uint8Array {
          const data = files.get(path);
          if (!data) throw hostError(`File '${path}' not found`);
          return data;
        },
        readFileChunk(path: string, offset: bigint, length: number): Uint8Array {
          const data = files.get(path);
          if (!data) throw hostError(`File '${path}' not found`);
          const dataLength = BigInt(data.length);
          if (offset < 0n || offset >= dataLength || length <= 0) {
            return new Uint8Array(0);
          }
          const start = Number(offset);
          const end = Math.min(start + length, data.length);
          return new Uint8Array(data.buffer, data.byteOffset + start, end - start);
        },
        fileSize(path: string): bigint {
          const data = files.get(path);
          if (!data) throw hostError(`File '${path}' not found`);
          return BigInt(data.length);
        },
        writeFile(path: string, data: Uint8Array): void {
          files.set(path, new Uint8Array(data));
        },
        listFiles(dir: string): string[] {
          // One directory level, like enoch; "" is the root (EHQ-399).
          if (dir.split('/').includes('..')) throw hostError(`Invalid directory '${dir}'`);
          const base = dir.replace(/\/+$/, '');
          const prefix = base ? `${base}/` : '';
          return [...files.keys()].filter(k => k.startsWith(prefix) && !k.slice(prefix.length).includes('/'));
        },
      },
      'enlighten:module/host-log': {
        log(level: string, message: string): void {
          getLogCallback()(level, message);
        },
      },
      'enlighten:module/host-variables': {
        getVariable(name: string): string | undefined {
          const data = getData();
          if (!Object.prototype.hasOwnProperty.call(data, name)) return undefined;
          return JSON.stringify(data[name]);
        },
        setVariable(name: string, value: string): void {
          getData()[name] = decodeVariable(value);
        },
        listVariables(): [string, string][] {
          return Object.entries(getData()).map(([k, v]) => [k, JSON.stringify(v)]);
        },
      },
      'enlighten:module/host-api': {
        call: (service: string, method: string, argsJson: string): string => {
          if (!this.serviceCallFn) {
            throw hostError('host-api: service call not configured');
          }
          const projectId = this.projectIdFn();
          if (!projectId || projectId <= 0) {
            throw hostError(`host-api: invalid projectId '${projectId}'`);
          }
          try {
            return this.serviceCallFn(
              `${API_BASE}/project/${projectId}/service-call/${encodeURIComponent(service)}/${encodeURIComponent(method)}`,
              argsJson,
            );
          } catch (e) {
            // The XHR helper throws plain Errors; re-throw as the WIT Err arm.
            throw hostError(e instanceof Error ? e.message : String(e));
          }
        },
        // host-api.query (EHQ-285, browser leg EHQ-358): SQL against a project
        // connection, through ehq-server's step-query endpoint, as enoch does.
        query: (connection: string, sql: string, paramsJson: string): string => {
          const projectId = this.projectIdFn();
          if (!projectId || projectId <= 0) {
            throw hostError(`host-api: invalid projectId '${projectId}' (query on '${connection}')`);
          }
          try {
            return runStepQuery(this.queryPostFn, API_BASE, projectId, connection, sql, paramsJson);
          } catch (e) {
            // Every failure leaves as the WIT Err arm rather than as a trap.
            throw hostError(e instanceof Error ? e.message : String(e));
          }
        },
      },
    };

    // Instantiate the component
    const instance = await moduleJs.instantiate(getCoreModule, { ...imports, ...overrides });

    const manifestJson = instance.getManifest();
    const manifest: ModuleManifest = JSON.parse(manifestJson);

    return {
      manifest,
      getManifest: instance.getManifest,
      execute: instance.execute,
      tables,
      files,
    };
  }

  /** Get a loaded module by name */
  getModule(name: string): LoadedModule | undefined {
    return this.modules.get(name);
  }

  /** Get a loaded step interpreter by name. Never a project module. */
  getInterpreter(name: string): LoadedModule | undefined {
    return this.interpreters.get(name);
  }

  /** Drop a step interpreter, so the next load instantiates it afresh (after a trap). */
  unloadInterpreter(name: string): void {
    this.interpreters.delete(name);
  }

  /** List all loaded module manifests */
  listModules(): ModuleManifest[] {
    return [...this.modules.values()].map(m => m.manifest);
  }

  /** Execute a function on a loaded module */
  executeFunction(moduleName: string, functionName: string, argsJson: string): string {
    const mod = this.modules.get(moduleName);
    if (!mod) {
      throw new Error(`Module '${moduleName}' not loaded`);
    }
    return mod.execute(functionName, argsJson);
  }
}
